import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Dispatch, iRootState } from "../../../../rematch/store";
import { AxiosInstance } from "axios";
import { getValueListItemsAsync } from "../../../../backend/services/FilterDataFetchService";
import IMFilesPropertyDefinition from "../../../../backend/interfaces/mfiles/IMFilesPropertyDefinition";
import IMfilesValueListItem from "../../../../backend/interfaces/mfiles/IMfilesValueListItem";
import { translateValueListItemName } from "../../../../backend/helpers/translationHelpers";
import sortBy from "lodash/sortBy";
import { SelectOption } from "../FilterOperatorSelect";
import Select from "react-select";
import { useTranslation } from "react-i18next";

interface IValueListListProps {
  selectedValues: string[];
  propertyId: number;
  onChange: (valueListItems: IMfilesValueListItem[]) => void;
  isMultiSelect: boolean;

  // From connect()
  httpClient: AxiosInstance;
  getPropertyDefinition: (propertyId: number) => IMFilesPropertyDefinition;
  getValueListItems: (valueListId: number) => IMfilesValueListItem[];
  setValueListItems: (valueListId: number, valueListItems: IMfilesValueListItem[]) => void;
}

const ValueListList = (props: IValueListListProps) => {
  const { t } = useTranslation();
  
  const [items, setItems] = useState<IMfilesValueListItem[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState([]);

  const propDef = props.getPropertyDefinition(props.propertyId);

  const validValueListOptions: SelectOption[] = [];
  
  items.forEach((item: IMfilesValueListItem) => {
    validValueListOptions.push( { value: item.ID.toString(), label: translateValueListItemName(propDef.ValueList, item.ID, item.Name) } );
  });

  // Fetch all ValueList items when mounting
  useEffect(() => {
    const fetchValueListItemsAsync = async () => {
      const propertyDefinition = props.getPropertyDefinition(props.propertyId);
      let results = props.getValueListItems(propertyDefinition.ValueList);
      if(!results) {
        results = await getValueListItemsAsync(props.httpClient, propertyDefinition.ValueList);
        props.setValueListItems(propertyDefinition.ValueList, results);
      }
      const sortedResults = sortBy(results,(p) => translateValueListItemName(propertyDefinition.ValueList, p.ID, p.Name));
      setItems(sortedResults);
    };

    setItems([]);
    fetchValueListItemsAsync();
  }, [props.propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pick selected indexes every time fetched items or initial selected items change
  useEffect(() => {
    const newSelectedIndexes = [];
    const selectedItemsIds = props.selectedValues.map((valueString) => parseInt(valueString));
    const allItemsIds = items.map((item) => item.ID);
    for (let i = 0; i < allItemsIds.length; i++) {
      if (selectedItemsIds.includes(allItemsIds[i])) {
        newSelectedIndexes.push(i);
      }
    }
    setSelectedIndexes(newSelectedIndexes);
  }, [items, props.selectedValues]);


  return (

    <Select
      options={validValueListOptions}
      defaultValue={selectedIndexes.map( (idx) => validValueListOptions[idx] )}
      value={selectedIndexes.map( (idx) => validValueListOptions[idx] )}
      isMulti={props.isMultiSelect}
      placeholder={`${t("command.select")}...`}
      styles={{ 
        menuPortal: (base) => ({ ...base, zIndex: 9999 }), 
        control: (base) => ({ ...base, background: 'white', border: '1px solid #78899a', borderRadius: 'none' }),
        multiValue: (base) => ({ ...base, fontSize: "16px" }), //multiValue child has 85% font
        option: provided => ({ ...provided, color: 'black' }),
      }}
      onChange={(item: SelectOption | SelectOption[]) => {

        if ( ! props.isMultiSelect ) {

          const selectedIndex = validValueListOptions.findIndex((value: SelectOption) => value.value === (item as SelectOption).value)
          setSelectedIndexes([selectedIndex]);

          const selectedItem = items.find((value) => value.ID === parseInt( (item as SelectOption).value) );
          props.onChange( [selectedItem] );

        } else {

          const newSelectedIndexes = (item as SelectOption[]).map( (item, idx) => items[idx] );
          setSelectedIndexes(newSelectedIndexes);

          props.onChange( newSelectedIndexes );
        }

      }}
    />
  );
};

export default connect((rootState: iRootState) => ({
  httpClient: rootState.common.httpClient,
  getPropertyDefinition: (propertyId: number) => rootState.common.configuration.propertyDefinitions.get(propertyId),
  getValueListItems: (valueListId: number) => rootState.common.configuration.valueListItems[valueListId],
}),(dispatch: Dispatch) => ({
  setValueListItems: (valueListId: number, valueListItems: IMfilesValueListItem[]) => dispatch.common.setValueListItems(valueListId, valueListItems),
}))(ValueListList);
